import type { ApolloError } from '@apollo/client/errors'

import { STORY_STATUSES } from '@/constants/STORY_STATUSES.ts'
import { getStory } from '@/requests/apollo/cache.ts'
import { useRecalculateCurrentIterationVelocity, useUpdateCache } from '@/requests/apollo/useUpdateCache.ts'
import { useMoveStory } from '@/requests/mutations/useMoveStory.ts'
import { useMutateStory } from '@/requests/mutations/useMutateStory.ts'
import { isTemporaryId } from '@/utils/key.ts'
import { GlobalPubSub } from '@/utils/pubsub.ts'

export const useMutateStoryStatus = (storyId: string) => {
  const { moveStoryByStatus } = useMoveStory()
  const { recalculateCurrentIterationVelocity } = useRecalculateCurrentIterationVelocity()
  const { updateStory } = useUpdateCache()
  const { mutate } = useMutateStory(storyId)

  return (value: StoryStatus, onError?: (error: ApolloError) => void) => {
    GlobalPubSub.publish(GlobalPubSub.Signals.DESELECT_STORIES, storyId)
    const requestedParams = { status: value }
    const optimisticResponseParameters = {
      status: value,
      acceptedAt: value === STORY_STATUSES.ACCEPTED ? new Date().getTime() : null,
    }

    const story = getStory(storyId)
    if (!story) throw new Error(`Story not found: ${storyId}`)

    if (isTemporaryId(storyId)) {
      updateStory(storyId, optimisticResponseParameters)
      recalculateCurrentIterationVelocity()
      return Promise.resolve()
    }

    recalculateCurrentIterationVelocity()

    return mutate(requestedParams, {
      update: () => moveStoryByStatus(storyId, value as StoryStatus, story.status),
      onError,
      optimisticResponseParameters,
    })
  }
}
